import { elevationM, summarize, type Seafloor } from '../sim/seafloor';
import { DEPTH_BINS, REEF_FLOOR_M, REEF_ISLANDS, binFloorsKm2, depthBinOf, type Reef } from '../sim/reef';
import { smoothCurve, smoothstep, solveIncreasing } from './curve';
import { hash3 } from './fishSwim';

/**
 * The aquarium as one giant exhibit (M94, the user's word and their brief,
 * `docs/AQUARIUM_REDESIGN.md`): a wide tank seen from the side, the island on
 * the left, its reef, the slope, the twilight water and the deep to the
 * right, its habitats each given room. Pure arithmetic, so it is tested in
 * node (`tests/exhibit.test.ts`); `ExhibitView` draws it.
 *
 * **Its floor is the sanctuary's own: real samples of its reef, its slope and
 * its descent to the deep** (the brief: "identify actual reef, slope and
 * offshore habitat samples, then give those representative sections more
 * visual room"; `shoreProfiles`, all MEASURED from its 1 km columns):
 *
 * - **Near each island's shore, to `SPLICE_M`**, its median floor at each
 *   distance from its coast: every column put at its distance from the
 *   nearest coast and given to the island whose reef lies nearest it. Maui
 *   Nui's reach the reef's 30 m within about 0.9 km, and differ: Molokaʻi's
 *   broad shelf, Kahoʻolawe's floor 76 m deep half a kilometer out.
 * - **Beyond it, the real descent to the deepest water**: the straight line
 *   from the deepest column to the nearest coast, its floor as it is. At Maui
 *   Nui, from Lānaʻi's south-west coast to the box's south-west corner, 55 km:
 *   a ledge at about 597 to 645 m from 10 km to 20 km, a fall of 430 m in a
 *   kilometer from 673 m at 20.2 km, a bench at 2,357 to 2,519 m from 27 km
 *   to 38 km, a deep floor to its last sample's 4,220 m (MEASURED, M94's
 *   review). The medians, pooled over every direction, blurred
 *   these; no straight line from a reef reaches the deep without passing
 *   another island (4,139 tried, M94).
 *
 * Nothing is made broader or flatter than the samples are; they are only
 * stretched, and a little roughened below `ROUGH_BELOW_M` (texture, `ROUGH`).
 *
 * **Stretched, and it says so.** Depth through the brief's stops
 * (`DEPTH_STOPS`); distance from the coast by the same stretch: each
 * kilometer given width as the depth scale stretches the sanctuary's mean
 * floor's depth there (its medians spliced onto the descent, `kmToDepth`'s),
 * so that floor's every slope is drawn its real slope times one factor
 * (`exaggeration`, 10.8 to 1,000 m; 4 to 6 below 2,000 m, where the least
 * stretch takes over): the ledges flat, the drop-offs steep, the reef, where
 * depth is stretched the most, the most room. Each island's own floor, laid
 * on that one width, is stretched by its own depths there: to 200 m Molokaʻi's
 * slopes are drawn 11 to 27 times as steep as they are, Maui's 12 to 18,
 * Lānaʻi's 8 to 10, and Kahoʻolawe's, steeper than the mean near its shore,
 * 2 to 11 (MEASURED, M94's review). Both are smooth and one way, so they turn around
 * exactly: an animal 10 m above the real floor is drawn 10 m above the drawn
 * floor, and the ruler reads real depths. The real map stays the other view.
 *
 * **Its bands.** One band of the tank's depth, front to back, for each of the
 * four islands, holding that island's floor near its shore and its real reef
 * floor in all (`binFloorsKm2`), so its fish and corals are as many as on the
 * map (`exhibitCover`). The broadest reef stands at the front, nearest the
 * glass (JUDGED: the opening view shows the most reef).
 */

/** The tank, scene units: across (x, shore to offshore), its height (y), front to back (z). JUDGED: the reef's third of the width fills a computer's opening view. */
export const TANK = { width: 32, height: 12, depth: 8 } as const;
/** The display grid's vertices across and front to back, a fifth of a unit apart (JUDGED: finer than the 1 km data it draws everywhere offshore). */
export const COLS = 161;
export const ROWS = 41;

/** The brief's depth stops (the user's word, M94): each depth at its share of the tank's height from the surface; the deepest water at the floor. */
export const DEPTH_STOPS: readonly { m: number; share: number }[] = [
  { m: 0, share: 0 },
  { m: 30, share: 0.26 },
  { m: 200, share: 0.46 },
  { m: 1000, share: 0.75 },
];
/**
 * A shelf's depth stops (M98, JUDGED: a site whose floor stays shallower than `DEEP_SITE_M`, the Cape Peninsula's to
 * 1,006 m, a shelf with a slope at its edge; its reef and kelp depths to 30 m given most room, the shelf to 200 m,
 * its slope the rest), each depth at its share of the tank's height; the deepest water at the floor.
 */
const SHELF_STOPS: readonly { m: number; share: number }[] = [
  { m: 0, share: 0 },
  { m: 30, share: 0.3 },
  { m: 200, share: 0.65 },
];
/** The deepest a site's floor runs and still takes `SHELF_STOPS`, m (JUDGED: deeper, the brief's stops have room, Maui Nui's 4,234 m). */
const DEEP_SITE_M = 1500;
/** A site's depth stops: the brief's where its floor runs deep, a shelf's where it does not (M98). */
function stopsFor(deepestM: number): readonly { m: number; share: number }[] {
  return deepestM > DEEP_SITE_M ? DEPTH_STOPS : SHELF_STOPS;
}
/** The shallowest sanctuary an exhibit is drawn for, m: deeper than a shelf's last depth stop, so its stops keep their order (M98: was 1,000 m, the brief's last stop). */
export const EXHIBIT_MIN_DEPTH_M = 300;
/** The land shown inland of the coast at the tank's left, km, and the share of the tank's width it takes (JUDGED: the island's first hills, a backdrop to the reef). */
export const LAND_KM = 4;
export const COAST_SHARE = 0.08;
/** The step the width is shared out in, km (MEASURED: a tenth of it moves no share by a thousandth). */
const ACROSS_STEP_KM = 0.01;
/**
 * The least stretch a kilometer from the coast is drawn at, a share of the tank's height a meter: below it, the
 * far water, where depth is stretched least, would have almost no room, so there every slope is drawn less steep
 * than elsewhere (JUDGED).
 */
const LEAST_STRETCH = 0.00015;
/** The land's rise, a share of the tank's height a meter: the twilight zone's own scale (JUDGED: the islands' 400 m hills about a sixth of the tank's height). */
export const LAND_SHARE_PER_M = (0.75 - 0.46) / 800;
/** Where each island's own floor gives way to the descent, m deep, and over how far either side, km (JUDGED: below the slope, where the islands' flanks meet the basins they share). */
export const SPLICE_M = 300;
export const SPLICE_EASE_KM = 1;
/** The fewest columns (km²) a distance's median is taken from (JUDGED: a dozen square kilometers of floor). */
const MIN_CELLS = 12;
/** The step the descent is sampled at, km, between the columns' centers (JUDGED: four to a column). */
const DESCENT_STEP_KM = 0.25;
/** A reef column's floor, m: deeper than the surf's sand, to the reef's 30 m (`REEF_FLOOR_M`). */
export const REEF_TOP_M = 1;
/** How much the floor below `ROUGH_BELOW_M` is roughened, a share of its depth (JUDGED, texture only: the samples are smooth between columns). */
const ROUGH = 0.04;
const ROUGH_BELOW_M = 50;

/** A profile: elevation, m (negative below the sea), at distance from the coast, km (negative inland), increasing. */
export interface Profile {
  km: readonly number[];
  m: readonly number[];
}

/** The real samples: each island's median floor (in `REEF_ISLANDS` order), inland and offshore; the sanctuary's; and the descent to its deepest water. */
export interface ShoreProfiles {
  islands: readonly Profile[];
  pooled: Profile;
  descent: Profile;
  deepestM: number;
}

/** The median of a list (the upper of the middle two). */
function median(values: number[]): number {
  const s = [...values].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)] as number;
}

/** The distance bins, km from the coast: a kilometer wide inland and near the shore, wider offshore where the columns thin. */
const BINS = [-9, -8, -7, -6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 10, 12, 14, 16, 18, 20, 22.5, 25, 27.5, 30, 32.5, 35, 37.5, 40, 42.5, 45, 47.5, 50, 52.5, 55, 57.5, 60, 65, 70];

const memo = new WeakMap<Seafloor, ShoreProfiles>();

/**
 * The sanctuary's real samples (MEASURED from its 1 km columns; module-cached, as the floor never changes):
 *
 * - **Its medians**: a column is land where it is less than half water; its distance is to the nearest column of
 *   the other kind, center to center, less half a column, so the coast lies between them; its island is that of the
 *   nearest reef column. Each distance bin with `MIN_CELLS` columns or more gives a point, their median distance
 *   and median elevation; the coast is 0 m at 0 km.
 * - **Its descent**: from the land column nearest the deepest column, straight to it, the floor's elevation
 *   (`elevationM`, between the columns' centers) every `DESCENT_STEP_KM`, measured from where the line first comes
 *   under the sea. A line that comes to land again is no descent: the pooled median serves.
 */
export function shoreProfiles(real: Seafloor, reef: Reef | null): ShoreProfiles {
  const kept = memo.get(real);
  if (kept) return kept;
  const n = real.cells;
  const total = n * n;
  const land: number[] = [];
  const wet: number[] = [];
  for (let i = 0; i < total; i++) ((real.waterPercent[i] ?? 0) < 50 ? land : wet).push(i);
  const elevation = (i: number): number => ((real.waterPercent[i] ?? 0) < 50 ? real.landHeightM[i] ?? 0 : -(real.waterDepthM[i] ?? 0));
  const nearest = (i: number, among: readonly number[]): { km: number; at: number } => {
    const [x, y] = [i % n, Math.floor(i / n)];
    let best = Infinity;
    let at = -1;
    for (const j of among) {
      const d = ((j % n) - x) ** 2 + (Math.floor(j / n) - y) ** 2;
      if (d < best) {
        best = d;
        at = j;
      }
    }
    return { km: Math.sqrt(best) * real.cellKm - real.cellKm / 2, at };
  };
  // Each column's island, the nearest reef column's; one coast where a site has no reef (M98).
  const reefXY = reef ? reef.columns.map((c, j) => [c % n, Math.floor(c / n), reef.island[j] as number] as const) : [];
  const islands = reef ? REEF_ISLANDS.length : 1;
  const islandOf = (i: number): number => {
    const [x, y] = [i % n, Math.floor(i / n)];
    let best = Infinity;
    let island = 0;
    for (const [cx, cy, isl] of reefXY) {
      const d = (cx - x) ** 2 + (cy - y) ** 2;
      if (d < best) {
        best = d;
        island = isl;
      }
    }
    return island;
  };
  const binOf = (km: number): number => {
    for (let b = 0; b < BINS.length - 1; b++) if (km >= (BINS[b] as number) && km < (BINS[b + 1] as number)) return b;
    return -1;
  };
  type Bin = { km: number[]; m: number[] };
  const fresh = (): Bin[] => BINS.slice(1).map(() => ({ km: [], m: [] }));
  const own = Array.from({ length: islands }, fresh);
  const pooled = fresh();
  for (const i of wet) {
    const { km } = nearest(i, land);
    const b = binOf(km);
    if (b < 0) continue;
    for (const bins of [own[islandOf(i)] as Bin[], pooled]) {
      (bins[b] as Bin).km.push(km);
      (bins[b] as Bin).m.push(elevation(i));
    }
  }
  for (const i of land) {
    const km = -nearest(i, wet).km;
    const b = binOf(km);
    if (b < 0) continue;
    const bin = (own[islandOf(i)] as Bin[])[b] as Bin;
    bin.km.push(km);
    bin.m.push(elevation(i));
  }
  const profileOf = (bins: Bin[]): Profile => {
    const points = bins.filter((b) => b.km.length >= MIN_CELLS).map((b) => [median(b.km), median(b.m)] as const);
    const all = [...points, [0, 0] as const].sort((a, b) => a[0] - b[0]);
    return { km: all.map((p) => p[0]), m: all.map((p) => p[1]) };
  };
  const pooledProfile = profileOf(pooled);
  // The descent: from the land column nearest the deepest, straight to it.
  let deepest = 0;
  for (const i of wet) if ((real.waterDepthM[i] ?? 0) > (real.waterDepthM[deepest] ?? 0)) deepest = i;
  const from = nearest(deepest, land).at;
  const at = (x: number, y: number): number => {
    const cx = Math.max(0, Math.min(n - 1.000001, x));
    const cy = Math.max(0, Math.min(n - 1.000001, y));
    const [i, j] = [Math.floor(cx), Math.floor(cy)];
    const [s, t] = [cx - i, cy - j];
    const e = (a: number, b: number): number => elevationM(real, b * n + a);
    return (e(i, j) * (1 - s) + e(i + 1, j) * s) * (1 - t) + (e(i, j + 1) * (1 - s) + e(i + 1, j + 1) * s) * t;
  };
  const [x0, y0, x1, y1] = [from % n, Math.floor(from / n), deepest % n, Math.floor(deepest / n)];
  const lengthKm = Math.hypot(x1 - x0, y1 - y0) * real.cellKm;
  const km: number[] = [0];
  const m: number[] = [0];
  let coast = -1;
  let before = at(x0, y0);
  let landed = false;
  for (let t = DESCENT_STEP_KM; t <= lengthKm + 1e-9; t += DESCENT_STEP_KM) {
    const e = at(x0 + ((x1 - x0) * t) / lengthKm, y0 + ((y1 - y0) * t) / lengthKm);
    if (coast < 0) {
      // Where the line comes under the sea, between this sample and the last.
      if (e < 0) coast = t - DESCENT_STEP_KM + (DESCENT_STEP_KM * before) / (before - e);
      else {
        before = e;
        continue;
      }
    } else if (e >= 0) landed = true;
    km.push(t - coast);
    m.push(e);
  }
  const made: ShoreProfiles = {
    islands: own.map(profileOf),
    pooled: pooledProfile,
    descent: landed || km.length < 3 ? pooledProfile : { km, m },
    deepestM: summarize(real).deepestM,
  };
  memo.set(real, made);
  return made;
}

/** A habitat the camera can go to (the brief's, Maui Nui's): its name, and its box as shares of the tank, across and down from the surface. */
export interface Habitat {
  id: 'reef' | 'slope' | 'blue' | 'twilight' | 'deep' | 'rocky' | 'shelf';
  name: string;
  across: readonly [number, number];
  down: readonly [number, number];
}

/** The exhibit of a sanctuary: its floor on the display grid, its reef there, its bands, its transforms and its habitats. */
export interface Exhibit {
  /** Each vertex's real elevation, m (negative below the sea), row by row from the back, `COLS` to a row. */
  elevationM: Float64Array;
  /** Each vertex's real floor slope across its band's profile, degrees (the real one, not as drawn). */
  slopeDeg: Float64Array;
  /** The reef on the display grid: its columns are vertices. */
  reef: Reef;
  /** Each band's island (`REEF_ISLANDS` index) and its middle row, back to front. */
  bands: readonly { island: number; row: number }[];
  deepestM: number;
  /** The real distance the tank's width spans from the coast, km. */
  farKm: number;
  /** How many times steeper every slope is drawn than it is (the tank's own height and width against the real ones'). */
  exaggeration: number;
  /** A real depth's share of the tank's height from the surface, and back. */
  depthShare(m: number): number;
  depthAt(share: number): number;
  /** A real distance from the coast's share of the tank's width, and back. */
  across(km: number): number;
  kmAt(share: number): number;
  /** The real distance at which the exhibit's floor (its reference, the sanctuary's medians spliced to the descent) first reaches a depth, km. */
  kmToDepth(m: number): number;
  habitats: readonly Habitat[];
}

/** A smooth noise on the plane, -1 to 1, from the sprites' hash. */
function noise(x: number, y: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const fx = x - xi;
  const fy = y - yi;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const v = (i: number, j: number): number => hash3(xi + i, yi + j, seed) * 2 - 1;
  return (v(0, 0) * (1 - sx) + v(1, 0) * sx) * (1 - sy) + (v(0, 1) * (1 - sx) + v(1, 1) * sx) * sy;
}

/** The first distance from the coast, km, at which a profile's floor is `m` deep: stepped by 50 m, then halved to a millimeter. */
function firstReach(f: (km: number) => number, m: number, farKm: number): number {
  for (let km = 0.05; km <= farKm; km += 0.05) {
    if (-f(km) < m) continue;
    let [a, b] = [km - 0.05, km];
    while (b - a > 1e-6) {
      const mid = (a + b) / 2;
      if (-f(mid) >= m) b = mid;
      else a = mid;
    }
    return b;
  }
  return farKm;
}

/** The exhibit of a sanctuary's seafloor and reef. */
export function exhibitOf(real: Seafloor, realReef: Reef | null): Exhibit {
  const profiles = shoreProfiles(real, realReef);
  const { deepestM } = profiles;
  const stops = stopsFor(deepestM);
  const depthCurve = smoothCurve([...stops.map((s) => s.m), deepestM], [...stops.map((s) => s.share), 1]);
  const depthShare = (m: number): number => depthCurve(Math.min(Math.max(m, 0), deepestM));
  const depthAt = (share: number): number => solveIncreasing(depthCurve, Math.min(Math.max(share, 0), 1), 0, deepestM);
  const descent = smoothCurve(profiles.descent.km, profiles.descent.m);
  const descentKm = profiles.descent.km[profiles.descent.km.length - 1] as number;
  const intoDescent = firstReach(descent, SPLICE_M, descentKm);
  // A floor near a shore spliced onto the descent where each reaches `SPLICE_M`, eased across `SPLICE_EASE_KM` either
  // side; inland, the shore's own.
  const spliced = (shore: Profile): { f: (km: number) => number; spliceKm: number } => {
    const near = smoothCurve(shore.km, shore.m);
    const lastKm = shore.km[shore.km.length - 1] as number;
    const spliceKm = firstReach(near, SPLICE_M, lastKm);
    return {
      spliceKm,
      f: (km: number): number => {
        if (km <= 0) return near(km);
        const w = smoothstep(spliceKm - SPLICE_EASE_KM, spliceKm + SPLICE_EASE_KM, km);
        return (1 - w) * near(km) + w * descent(km - spliceKm + intoDescent);
      },
    };
  };
  const reference = spliced(profiles.pooled);
  const farKm = reference.spliceKm + descentKm - intoDescent;
  const kmToDepth = (m: number): number => firstReach(reference.f, m, farKm);
  // Each kilometer from the coast is given width as the depth scale stretches the reference floor's depth there
  // (`ACROSS_STEP_KM` at a time), so its every slope is drawn its real slope times one factor; the land's share laid on
  // evenly.
  const steps = Math.ceil(farKm / ACROSS_STEP_KM);
  const step = farKm / steps;
  const room = new Float64Array(steps + 1);
  for (let i = 1; i <= steps; i++) {
    const z = Math.max(0, -reference.f((i - 0.5) * step));
    room[i] = (room[i - 1] as number) + Math.max(depthShare(z + 0.5) - depthShare(Math.max(0, z - 0.5)), LEAST_STRETCH);
  }
  const whole = room[steps] as number;
  const across = (km: number): number => {
    if (km <= 0) return COAST_SHARE * Math.max(0, 1 + km / LAND_KM);
    const f = Math.min(steps, km / step);
    const i = Math.min(steps - 1, Math.floor(f));
    const r = (room[i] as number) + ((room[i + 1] as number) - (room[i] as number)) * (f - i);
    return COAST_SHARE + ((1 - COAST_SHARE) * r) / whole;
  };
  const kmAt = (share: number): number => solveIncreasing(across, Math.min(Math.max(share, 0), 1), -LAND_KM, farKm);
  // A real slope drawn: its drop, H times the depth's stretch a meter, against its run, the width's share of
  // `whole` a step (1,000 m a km) at the same stretch, which cancels.
  const exaggeration = (1000 * TANK.height * step * whole) / (TANK.width * (1 - COAST_SHARE));
  // Each island's floor, and the bands back to front, the broadest reef (the farthest its floor stays within 30 m)
  // at the front.
  const islandFloors = profiles.islands.map((p) => spliced(p).f);
  const reefKm = islandFloors.map((f) => firstReach(f, REEF_FLOOR_M, farKm));
  const order = islandFloors.map((_, i) => i).sort((a, b) => (reefKm[a] as number) - (reefKm[b] as number) || a - b);
  const perBand = ROWS / order.length;
  const bands = order.map((island, k) => ({ island, row: Math.round((k + 0.5) * perBand - 0.5) }));
  // A row's place among the bands' middles: each band's own floor across the middle half of it, eased into its
  // neighbor's across the rest.
  const rowBlend = (row: number): { a: number; b: number; w: number } => {
    const at = (row + 0.5) / perBand - 0.5;
    const a = Math.max(0, Math.min(order.length - 1, Math.floor(at)));
    const b = Math.min(order.length - 1, a + 1);
    return { a, b, w: a === b ? 0 : smoothstep(0.25, 0.75, at - a) };
  };
  const elevationM = new Float64Array(COLS * ROWS);
  const slopeDeg = new Float64Array(COLS * ROWS);
  for (let r = 0; r < ROWS; r++) {
    const { a, b, w } = rowBlend(r);
    const fa = islandFloors[order[a] as number] as (km: number) => number;
    const fb = islandFloors[order[b] as number] as (km: number) => number;
    const f = (km: number): number => Math.max(-deepestM, (1 - w) * fa(km) + w * fb(km));
    for (let c = 0; c < COLS; c++) {
      const km = kmAt(c / (COLS - 1));
      let e = f(km);
      if (e < -ROUGH_BELOW_M) e = Math.max(-deepestM, e * (1 + ROUGH * noise((c / (COLS - 1)) * 14, r / 6, 94)));
      elevationM[r * COLS + c] = e;
      slopeDeg[r * COLS + c] = (Math.atan(Math.abs(f(km + 0.1) - f(km - 0.1)) / 200) * 180) / Math.PI;
    }
  }
  // Each island's real reef floor, km², shared among its band's reef columns, each in its own depth's bin; none where
  // the site has no reef.
  if (!realReef) {
    const reef: Reef = { ...NO_REEF };
    return { elevationM, slopeDeg, reef, bands, deepestM, farKm, exaggeration, depthShare, depthAt, across, kmAt, kmToDepth, habitats: shelfHabitats(across, kmToDepth, depthShare) };
  }
  const bins = binFloorsKm2(realReef);
  const islandKm2 = REEF_ISLANDS.map((_, isl) => realReef.columns.reduce((sum, _col, j) => (realReef.island[j] === isl ? sum + bins.reduce((t, bin) => t + (bin[j] ?? 0), 0) : sum), 0));
  const bandOfRow = (row: number): number => order[Math.max(0, Math.min(order.length - 1, Math.round((row + 0.5) / perBand - 0.5)))] as number;
  const columns: number[] = [];
  const island: number[] = [];
  for (let i = 0; i < COLS * ROWS; i++) {
    const depth = -(elevationM[i] as number);
    if (depth > REEF_TOP_M && depth <= REEF_FLOOR_M) {
      columns.push(i);
      island.push(bandOfRow(Math.floor(i / COLS)));
    }
  }
  const counted = REEF_ISLANDS.map((_, isl) => island.filter((x) => x === isl).length);
  const floor = DEPTH_BINS.map(() => columns.map(() => 0));
  columns.forEach((col, j) => {
    const isl = island[j] as number;
    const bin = DEPTH_BINS.findIndex((x) => x.bin === depthBinOf(-(elevationM[col] as number)));
    (floor[bin] as number[])[j] = (islandKm2[isl] as number) / Math.max(1, counted[isl] as number);
  });
  const reef: Reef = { ...realReef, columns, island, shallowKm2: floor[0] as number[], midKm2: floor[1] as number[], deepKm2: floor[2] as number[] };
  // The habitats, each by its real depths (the redesign's table, `AQUARIUM_REDESIGN.md` 3.1; M104: the reef had been
  // framed to 40 m and the upper slope 25 to 300 m) and the distances at which the floor reaches them: the shallow
  // reef from just inland to where the floor reaches 30 m, from just above the surface to 30 m; the slope, 30 to 400 m,
  // where the floor runs from 30 m to 400 m (M215, the user's word: "maybe you can change the upper slope to just the
  // slope so that it includes the deep 7?": its mesophotic reef and the Deep 7's floor, "roughly 100–400 m depth",
  // Syslo et al. 2024, p. 1, CC0; it had been the upper slope, 30 to 150 m); the sunlit open ocean over the slope, the
  // surface to 200 m, from where the floor reaches 100 m to where it reaches 2,000 m (JUDGED: its blue beyond the
  // reef); the twilight water, 200 to 1,000 m, from where the floor reaches 200 m to the far end; and the deep,
  // 1,000 m to the floor, from where it reaches 1,000 m.
  const habitats: Habitat[] = [
    { id: 'reef', name: 'Maui Nui Reef', across: [across(-0.6), across(kmToDepth(30))], down: [-0.06, depthShare(30)] },
    { id: 'slope', name: 'Slope', across: [across(kmToDepth(30)), across(kmToDepth(400))], down: [depthShare(30), depthShare(400)] },
    { id: 'blue', name: 'Blue water', across: [across(kmToDepth(100)), across(kmToDepth(2000))], down: [0, depthShare(200)] },
    { id: 'twilight', name: 'Twilight', across: [across(kmToDepth(200)), 1], down: [depthShare(200), depthShare(1000)] },
    { id: 'deep', name: 'Deep ocean', across: [across(kmToDepth(1000)), 1], down: [depthShare(1000), 1] },
  ];
  return { elevationM, slopeDeg, reef, bands, deepestM, farKm, exaggeration, depthShare, depthAt, across, kmAt, kmToDepth, habitats };
}

/** A reef with no columns: the exhibit's where a site has none (M98). */
const NO_REEF: Reef = {
  source: '', surveySource: '', waterSource: '', hotDayCoolingCPerM: 0, warmestMonthCoolingCPerM: 0,
  binDepthM: [], columns: [], island: [], shallowKm2: [], midKm2: [], deepKm2: [], surveys: [],
};

/**
 * A shelf's habitats (M98, JUDGED, each by its depths, as Maui Nui's): its rocky reef from just inland to where the
 * floor reaches 40 m, from just above the surface to 40 m (its kelp's depths, where kelp is drawn); the shelf, 25 to
 * 200 m, where the floor runs from 30 m to 200 m; the open water over the shelf's edge, the surface to 200 m, from
 * where the floor reaches 100 m to the far end; the slope, 150 m to the floor, from where it reaches 200 m.
 */
function shelfHabitats(across: (km: number) => number, kmToDepth: (m: number) => number, depthShare: (m: number) => number): Habitat[] {
  return [
    { id: 'rocky', name: 'Rocky reef', across: [across(-0.6), across(kmToDepth(40))], down: [-0.06, depthShare(40)] },
    { id: 'shelf', name: 'Shelf', across: [across(kmToDepth(30)), across(kmToDepth(200))], down: [depthShare(25), depthShare(200)] },
    { id: 'blue', name: 'Open water', across: [across(kmToDepth(100)), 1], down: [0, depthShare(200)] },
    { id: 'slope', name: 'Slope', across: [across(kmToDepth(200)), 1], down: [depthShare(150), 1] },
  ];
}

/**
 * The real reef's cover and bleaching laid on the exhibit's: each island's
 * live coral as a share of its floor (its columns' cover weighted by their
 * floor), and the share of its coral bleached (weighted by their coral), on
 * every column of its band (M86's rule, carried from the diorama); and the
 * share of its coral before a heat episode under way the episode has killed
 * (weighted by their coral before it, M110).
 */
export function exhibitCover(realReef: Reef, coverPercent: readonly number[], bleachedPercent: readonly number[], exhibitReef: Reef, killedPercent: readonly number[] = []): { cover: number[]; bleached: number[]; killed: number[] } {
  const bins = binFloorsKm2(realReef);
  const floorKm2 = realReef.columns.map((_, j) => bins.reduce((a, b) => a + (b[j] ?? 0), 0));
  const per = REEF_ISLANDS.map((_, isl) => {
    let floor = 0;
    let live = 0;
    let bleached = 0;
    let before = 0;
    realReef.columns.forEach((_col, j) => {
      if (realReef.island[j] !== isl) return;
      const f = floorKm2[j] as number;
      const l = (f * (coverPercent[j] ?? 0)) / 100;
      floor += f;
      live += l;
      bleached += (l * (bleachedPercent[j] ?? 0)) / 100;
      before += l / (1 - (killedPercent[j] ?? 0) / 100);
    });
    return { cover: floor > 0 ? (100 * live) / floor : 0, bleached: live > 0 ? (100 * bleached) / live : 0, killed: before > 0 ? (100 * (before - live)) / before : 0 };
  });
  return {
    cover: exhibitReef.island.map((isl) => per[isl]?.cover ?? 0),
    bleached: exhibitReef.island.map((isl) => per[isl]?.bleached ?? 0),
    killed: exhibitReef.island.map((isl) => per[isl]?.killed ?? 0),
  };
}
